import React, { useState, useEffect, useRef } from "react";
import { border, navy, red, surface, muted, danger } from "./constants";

// ── Shared input style ────────────────────────────────────────────────────────
export const inp = {
  background: "#fff",
  border: `1px solid ${border}`,
  borderRadius: 3,
  color: navy,
  padding: "12px 16px",
  fontSize: 16,
  fontFamily: "'DM Mono', monospace",
  width: "100%",
  outline: "none",
  WebkitAppearance: "none",
};

export const Input = React.forwardRef(function Input({ style, ...p }, ref) {
  return <input ref={ref} style={{ ...inp, ...style }} {...p} />;
});

export function Textarea({ style, ...p }) {
  return <textarea style={{ ...inp, resize: "vertical", height: 70, ...style }} {...p} />;
}

// ── Btn ───────────────────────────────────────────────────────────────────────
export function Btn({ variant = "red", style, children, ...p }) {
  const base = {
    border: "1px solid", borderRadius: 4, cursor: "pointer",
    fontSize: 14, letterSpacing: "0.12em", textTransform: "uppercase",
    padding: "15px 28px", transition: "all 0.15s",
    fontFamily: "'DM Mono', monospace", WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  };
  const v = {
    red:    { background: red,    color: "#fff",    borderColor: red    },
    ghost:  { background: "transparent", color: red, borderColor: red   },
    navy:   { background: navy,   color: "#f2ede4", borderColor: navy   },
    danger: { background: "transparent", color: danger, borderColor: danger },
  };
  return <button style={{ ...base, ...v[variant], ...style }} {...p}>{children}</button>;
}

// ── Label ─────────────────────────────────────────────────────────────────────
export function Label({ children, style }) {
  return (
    <div style={{ fontSize: 12, letterSpacing: "0.18em", color: navy, opacity: 0.55,
      textTransform: "uppercase", marginBottom: 7,
      fontFamily: "'DM Mono', monospace", ...style }}>
      {children}
    </div>
  );
}

// ── PortraitCropper ───────────────────────────────────────────────────────────
export function PortraitCropper({ src, cropData, onChange, onClose }) {
  const SIZE = 220;
  const [minZoom, setMinZoom] = useState(1);
  const [ready, setReady] = useState(false);
  const [natW, setNatW] = useState(1);
  const [natH, setNatH] = useState(1);
  const zoomRef = useRef(cropData?.zoom ?? 1);
  const oxRef   = useRef(cropData?.ox   ?? 0);
  const oyRef   = useRef(cropData?.oy   ?? 0);
  const imgRef  = useRef();
  const [, tick] = useState(0);
  const redraw = () => tick(n => n + 1);

  const pointersRef      = useRef({});
  const lastPinchDistRef = useRef(null);

  function computeMinZoom(nw, nh) { return Math.max(SIZE / nw, SIZE / nh); }

  function clampOffset(ox, oy, z, nw, nh) {
    const maxX = Math.max(0, (nw * z - SIZE) / 2);
    const maxY = Math.max(0, (nh * z - SIZE) / 2);
    return {
      ox: Math.min(maxX, Math.max(-maxX, ox)),
      oy: Math.min(maxY, Math.max(-maxY, oy)),
    };
  }

  function onImgLoad(e) {
    const nw = e.target.naturalWidth, nh = e.target.naturalHeight;
    setNatW(nw); setNatH(nh);
    const mz = computeMinZoom(nw, nh);
    setMinZoom(mz);
    if (!cropData) {
      zoomRef.current = mz; oxRef.current = 0; oyRef.current = 0;
    } else {
      const z = Math.max(mz, cropData.zoom ?? mz);
      zoomRef.current = z;
      const c = clampOffset(cropData.ox ?? 0, cropData.oy ?? 0, z, nw, nh);
      oxRef.current = c.ox; oyRef.current = c.oy;
    }
    setReady(true); redraw();
  }

  function onPointerDown(e) {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointersRef.current[e.pointerId] = { x: e.clientX, y: e.clientY };
    lastPinchDistRef.current = null;
  }

  function onPointerMove(e) {
    const pts = pointersRef.current;
    if (!pts[e.pointerId]) return;
    const ids = Object.keys(pts);
    if (ids.length === 1) {
      const dx = e.clientX - pts[e.pointerId].x;
      const dy = e.clientY - pts[e.pointerId].y;
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      const c = clampOffset(oxRef.current + dx, oyRef.current + dy, zoomRef.current, natW, natH);
      oxRef.current = c.ox; oyRef.current = c.oy; redraw();
    } else if (ids.length === 2) {
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      const [id0, id1] = ids;
      const p0 = pts[id0], p1 = pts[id1];
      const dist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
      if (lastPinchDistRef.current !== null) {
        const newZ = Math.max(minZoom, Math.min(minZoom * 4, zoomRef.current * (dist / lastPinchDistRef.current)));
        const c = clampOffset(oxRef.current, oyRef.current, newZ, natW, natH);
        zoomRef.current = newZ; oxRef.current = c.ox; oyRef.current = c.oy; redraw();
      }
      lastPinchDistRef.current = dist;
    }
  }

  function onPointerUp(e) { delete pointersRef.current[e.pointerId]; lastPinchDistRef.current = null; }

  function onWheel(e) {
    e.preventDefault();
    const newZ = Math.max(minZoom, Math.min(minZoom * 4, zoomRef.current * (1 - e.deltaY * 0.001)));
    const c = clampOffset(oxRef.current, oyRef.current, newZ, natW, natH);
    zoomRef.current = newZ; oxRef.current = c.ox; oyRef.current = c.oy; redraw();
  }

  const containerRef = useRef();
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  });

  function save() {
    onChange({ zoom: zoomRef.current, ox: oxRef.current, oy: oyRef.current, natW, natH });
    onClose();
  }

  const z = zoomRef.current;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)", zIndex: 300,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ color: "#f2ede4", fontFamily: "'Bebas Neue', sans-serif", fontSize: 20,
        letterSpacing: "0.2em", marginBottom: 20 }}>ADJUST PHOTO</div>
      <div ref={containerRef} style={{ width: SIZE, height: SIZE, borderRadius: "50%", overflow: "hidden",
        border: `3px solid ${red}`, position: "relative", cursor: "grab",
        touchAction: "none", userSelect: "none", flexShrink: 0, background: "#111" }}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove}
        onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
        <img src={src} alt="" onLoad={onImgLoad} ref={imgRef}
          style={{ position: "absolute", width: natW * z, height: natH * z,
            left: "50%", top: "50%",
            transform: `translate(calc(-50% + ${oxRef.current}px), calc(-50% + ${oyRef.current}px))`,
            pointerEvents: "none", draggable: false, maxWidth: "none", opacity: ready ? 1 : 0 }} />
      </div>
      <div style={{ fontSize: 13, color: "#a8b4cc", fontFamily: "'DM Mono', monospace",
        marginTop: 20, marginBottom: 28, textAlign: "center", lineHeight: 1.6 }}>
        Drag to reposition<br />
        <span style={{ opacity: 0.7 }}>Pinch to zoom (mobile) · Scroll to zoom (PC)</span>
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save}>Apply</Btn>
      </div>
    </div>
  );
}

// ── Thumb ─────────────────────────────────────────────────────────────────────
export function Thumb({ src, onRemove, onFeature, isFeature, size = 80, onClick }) {
  return (
    <div style={{ position: "relative", display: "inline-block", touchAction: "manipulation" }}>
      <img src={src} alt="" onClick={onClick}
        style={{ width: size, height: size, objectFit: "cover", borderRadius: 3,
          border: `2px solid ${isFeature ? "#e8a020" : border}`,
          cursor: onClick ? "zoom-in" : "default", display: "block" }} />
      {onRemove && (
        <button onClick={onRemove} style={{ position: "absolute", top: -5, right: -5,
          width: 20, height: 20, borderRadius: "50%", background: danger, border: "none",
          color: "#fff", cursor: "pointer", fontSize: 11, display: "flex",
          alignItems: "center", justifyContent: "center", touchAction: "manipulation" }}>✕</button>
      )}
      {onFeature && (
        <button onClick={onFeature} title="Set as feature photo"
          style={{ position: "absolute", top: -5, left: -5, width: 20, height: 20,
            borderRadius: "50%", background: isFeature ? "#e8a020" : "rgba(26,37,64,0.6)",
            border: "none", color: "#fff", cursor: "pointer", fontSize: 11,
            display: "flex", alignItems: "center", justifyContent: "center",
            touchAction: "manipulation" }}>★</button>
      )}
    </div>
  );
}

// ── Avatar ────────────────────────────────────────────────────────────────────
export function Avatar({ photo, cropData, size = 52 }) {
  const zoom = cropData?.zoom ?? 1;
  const ox   = cropData?.ox   ?? 0;
  const oy   = cropData?.oy   ?? 0;
  const natW = cropData?.natW;
  const natH = cropData?.natH;
  const CROP_SIZE = 220;
  const scale = size / CROP_SIZE;
  return (
    <div style={{ width: size, height: size, borderRadius: "50%",
      border: `2px solid ${red}50`, overflow: "hidden", background: surface,
      flexShrink: 0, position: "relative" }}>
      {photo
        ? <img src={photo} alt="" style={{
            position: "absolute",
            width:  natW ? natW * zoom * scale : size * zoom,
            height: natH ? natH * zoom * scale : size * zoom,
            top: "50%", left: "50%",
            transform: `translate(calc(-50% + ${ox * scale}px), calc(-50% + ${oy * scale}px))`,
            pointerEvents: "none", maxWidth: "none",
          }} />
        : <span style={{ position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%,-50%)", fontSize: size * 0.38,
            color: muted, fontFamily: "'DM Mono', monospace" }}>?</span>
      }
    </div>
  );
}

// ── FancyDropdown ─────────────────────────────────────────────────────────────
export function FancyDropdown({ value, onChange, options, placeholder = "All" }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btnRef = useRef();
  const ref    = useRef();

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function handleOpen() {
    if (!open && btnRef.current) setRect(btnRef.current.getBoundingClientRect());
    setOpen(o => !o);
  }

  const selected = options.find(o => o.value === value);

  return (
    <div ref={ref} style={{ position: "relative", userSelect: "none", width: "100%" }}>
      <div ref={btnRef} onClick={handleOpen} style={{
        background: open
          ? "linear-gradient(180deg, #2e4466 0%, #1e3454 100%)"
          : "linear-gradient(180deg, #263a58 0%, #1a2d48 100%)",
        borderRadius: open ? "10px 10px 0 0" : 10,
        padding: "9px 12px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        cursor: "pointer",
        boxShadow: open
          ? "0 1px 0 #0e1c30, 0 2px 4px rgba(0,0,0,0.24), inset 0 1px 3px rgba(0,0,0,0.2)"
          : "0 3px 0 #0e1c30, 0 4px 7px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.1)",
        transition: "border-radius 0.15s, box-shadow 0.12s",
      }}>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12,
          color: selected ? "#f2ede4" : "rgba(240,235,224,0.55)", letterSpacing: "0.03em",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>
          {selected ? selected.label : placeholder}
        </span>
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s",
            color: "rgba(240,235,224,0.55)", flexShrink: 0, marginLeft: 6 }}>
          <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      {open && rect && (
        <div style={{ position: "fixed", top: rect.bottom, left: rect.left, width: rect.width,
          zIndex: 9999, background: "#f8f4ee", borderRadius: "0 0 10px 10px",
          boxShadow: "4px 6px 16px rgba(0,0,0,0.15), -2px 4px 10px rgba(0,0,0,0.08)",
          maxHeight: 220, overflowY: "auto" }}>
          {[{ value: "", label: placeholder }, ...options].map((o, i) => {
            const isSelected    = o.value === value;
            const isPlaceholder = o.value === "" && i === 0;
            return (
              <div key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
                style={{ padding: "9px 12px", fontFamily: "'DM Mono', monospace", fontSize: 12,
                  color: isPlaceholder ? "#8a9aaa" : navy,
                  fontStyle: isPlaceholder ? "italic" : "normal",
                  fontWeight: isSelected && !isPlaceholder ? 700 : 400,
                  cursor: "pointer", borderTop: i === 0 ? "none" : "1px solid rgba(0,0,0,0.05)",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: "transparent", transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#e0d9cc"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                {o.label}
                {isSelected && !isPlaceholder && <span style={{ color: red, fontSize: 10 }}>✦</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── SlotSwitch ────────────────────────────────────────────────────────────────
export function SlotSwitch({ options, value, onChange }) {
  if (options.length > 2) {
    return (
      <div style={{ display: "flex", gap: 4 }}>
        {options.map(opt => (
          <div key={opt.value} onClick={() => onChange(opt.value)}
            style={{ fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
              fontFamily: "'DM Mono', monospace", cursor: "pointer",
              padding: "3px 9px", borderRadius: 4,
              background: value === opt.value ? red : "#1a2540",
              color: value === opt.value ? "#fff" : muted,
              fontWeight: value === opt.value ? "bold" : "normal",
              border: `1.5px solid ${value === opt.value ? red : "#ffffff18"}`,
              transition: "all 0.15s", userSelect: "none", whiteSpace: "nowrap" }}>
            {opt.label}
          </div>
        ))}
      </div>
    );
  }
  const isLeft = value !== options[1].value;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div onClick={() => onChange(options[0].value)}
        style={{ fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
          fontFamily: "'DM Mono', monospace", cursor: "pointer",
          color: value === options[0].value ? red : muted,
          fontWeight: value === options[0].value ? "bold" : "normal",
          transition: "color 0.18s", userSelect: "none", whiteSpace: "nowrap",
          minWidth: 52, textAlign: "right" }}>
        {options[0].label}
      </div>
      <div style={{ position: "relative", width: 56, height: 26, background: "#1a2540",
        borderRadius: 6, border: "1.5px solid #ffffff18",
        boxShadow: "inset 0 1px 4px rgba(0,0,0,0.5)", cursor: "pointer", flexShrink: 0 }}>
        <div style={{ position: "absolute", top: "50%", left: 4, right: 4, height: 2,
          transform: "translateY(-50%)", background: "#ffffff12", borderRadius: 1 }} />
        <div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)",
          left: isLeft ? 4 : undefined, right: isLeft ? undefined : 4,
          width: 18, height: 18, borderRadius: 4,
          background: "linear-gradient(145deg, #c0342b, #8a1a14)",
          border: "1.5px solid #e04040", boxShadow: "0 1px 6px rgba(192,52,43,0.7)",
          transition: "left 0.18s ease, right 0.18s ease", pointerEvents: "none" }} />
        <div onClick={() => onChange(options[0].value)}
          style={{ position: "absolute", top: 0, left: 0, width: "50%", height: "100%",
            borderRadius: "6px 0 0 6px", cursor: "pointer" }} />
        <div onClick={() => onChange(options[1].value)}
          style={{ position: "absolute", top: 0, right: 0, width: "50%", height: "100%",
            borderRadius: "0 6px 6px 0", cursor: "pointer" }} />
      </div>
      <div onClick={() => onChange(options[1].value)}
        style={{ fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
          fontFamily: "'DM Mono', monospace", cursor: "pointer",
          color: value === options[1].value ? red : muted,
          fontWeight: value === options[1].value ? "bold" : "normal",
          transition: "color 0.18s", userSelect: "none", whiteSpace: "nowrap" }}>
        {options[1].label}
      </div>
    </div>
  );
}
